import { ContactEdit, ContactEditRef } from "@shared/components/Molecules/ContactEdit/ContactEdit.component";
import { useTranslations } from "@shared/context/useTranslations";
import { useContact } from "@shared/context/ContactContext";
import { Contact, ContactState } from "@shared/types/UserAccountTypes.interface";
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { cn } from "@shared/helpers/classNames.helper";
import EmptyState from "@shared/components/Atoms/EmptyState/EmptyState.component";
import { ContactCard } from "../ContactCard/ContactCard.component";
import { matchContact } from "@shared/helpers/apis/contactHelper";
import { ContactSearch } from "../ContactSearch/ContactSearch.component";
import S from "./Contacts.module.scss";
import { fetchUserAccount } from "@shared/helpers/apis/userAccountHelper";

interface ContactsProps {
    onContactSelected?: (contact?: Contact) => void;
}

export type ContactsRef = {
    validate: () => ContactState;
    updateContact: (contact: Contact) => void;
};

export const Contacts = forwardRef<ContactsRef, ContactsProps>(({onContactSelected: _onContactSelected}: ContactsProps, ref) => {
    Contacts.displayName = "Contacts";

    const { getTranslation } = useTranslations();
    const { contacts, blank, primaryContact, updateContacts } = useContact();
    const [selectedId, setSelectedId] = useState<string | null>(primaryContact?.id || null);
    const [isEditMode, setIsEditMode] = useState<boolean>(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [showError, setShowError] = useState<boolean>(false);
    const [inputContact, setInputContact] = useState<Contact>(primaryContact || blank);
    const [contactList, setContactList] = useState<Contact[]>(contacts || []);
    const [shouldSaveContact, setShouldSaveContact] = useState<boolean>(false);

    const editRef = useRef<ContactEditRef>(null);
    const wasNewContactCreated = useRef<Contact | null>(null);

    useEffect(() => {
        const list = contacts
            ?.filter(contact => matchContact(contact, searchQuery))
            .sort((a, b) => Number(b.isPrimary) - Number(a.isPrimary));
        setContactList(list || [])

        // If a new contact was created, try to find it in the updated contacts list and select it
        if (wasNewContactCreated.current) {
            const newContact = contacts?.find(c => c.email === wasNewContactCreated.current?.email && c.firstName === wasNewContactCreated.current?.firstName && c.lastName === wasNewContactCreated.current?.lastName);
            if (newContact && newContact.id) {
                setSelectedId(newContact.id);
                wasNewContactCreated.current = null;
            }
        }
    }, [contacts, searchQuery]);

    useEffect(() => {
        const fetchPrimaryContact = async () => {
            if (!primaryContact) {
                const userAccountData = await fetchUserAccount();
                setInputContact({
                    firstName: "",
                    lastName: "",
                    email: userAccountData.email || "",
                    phone: "",
                    isPrimary: false
                });
                
            }
        };

        fetchPrimaryContact();
    }, [primaryContact]);

    // public methods
    useImperativeHandle(ref, () => ({
        validate: () => {
            if (isEditMode && editRef.current) {
                // Validate custom contact entered in checkout
                const validationResult = editRef.current?.validate();
                return {
                    ...validationResult,
                    saveContact: shouldSaveContact
                };
            } else {

                const foundContact = selectedId ? contacts?.find(item => item.id === selectedId) || null : null;

                setShowError(selectedId === null);

                return {
                    isValid: foundContact !== null,
                    contact: foundContact,
                    saveContact: false
                }
            }
        },
        updateContact: (contact: Contact) => {
            const list:Contact[] = [...contacts || [], contact];               
            updateContacts(list);
        }
    }));

    const handleToggleMode = () => {
        setIsEditMode(!isEditMode);
        setShowError(false);
    }

    const handleSelectContact = (contactId: string) => {
        setSelectedId(contactId);
        const contact = contacts?.find(c => c.id === contactId) || blank;
        setInputContact(contact);
        _onContactSelected?.(contact || undefined);
    }
    
    function handleSaveContact(e: React.MouseEvent<HTMLInputElement>): void {
        const isChecked = (e.target as HTMLInputElement).checked;
        // Contact should be saved on order complete
        setShouldSaveContact(isChecked);
    }

    return (
        <div className={S.contactsWrapper}>

            <div className={S.contactsHeader}>
                <div className={S.boxName}>{getTranslation("contactList.title")}</div>
                <button className={S.linkButton} onClick={handleToggleMode}>
                    {getTranslation(isEditMode ? "contactList.backToList": "contactList.chooseOther")}
                </button>
            </div>

            <div className={cn(S.expandWrapper, isEditMode && S.expanded)}>
                <div className={S.expandInner}>
                    <ContactEdit
                        ref={editRef}
                        useTwoColumns={true}
                        contact={inputContact}
                    />
                    <label htmlFor="saveContact" className={S.saveContactLabel}>
                        <input type="checkbox" name="saveContact" id="saveContact" onClick={(e) => {
                            handleSaveContact(e);
                            // (e.target as HTMLInputElement).checked = false;
                        }} />
                        {getTranslation("ContactEdit.saveContact")}
                    </label>

                </div>
            </div>

            <div className={cn(S.expandWrapper, !isEditMode && S.expanded)}>
                <div className={S.expandInner}>
                    <div className={S.listHeader}>
                        <h3>{getTranslation("contactList.listHeader")}</h3>
                        <ContactSearch onUpdate={(query: string) => setSearchQuery(query)} />
                    </div>

                    <div className={cn(S.listBox, showError && S.error)}>
                        {contactList && contactList?.length > 0 ? (
                            contactList?.map((contact) => (
                                <ContactCard
                                    key={contact.id}
                                    contact={contact}
                                    isSelected={contact.id === selectedId}
                                    onSelect={handleSelectContact}
                                />
                            ))
                        ) : (
                            <EmptyState heading={getTranslation("contactList.noItemsAvalible")} />
                        )}
                    </div>  
                    <div className={cn(S.errorMessage, showError && S.active)}>
                        {getTranslation("contactList.errorNoItemSelected")}
                    </div>
                </div>
            </div>
        </div>
    )
});
